import { useState } from 'react';
import { Search } from 'lucide-react';
import type {
  CostAccountingItem,
  DeltaResult,
  UnknownCostItem,
  VerificationCategory,
  VerificationResult,
} from '../../types/cost-accounting.js';

/** @graph-connects none */
function yen(amount: number): string {
  return `¥${amount.toLocaleString('ja-JP')}`;
}

/** @graph-connects none */
const CATEGORY_LABELS: Record<VerificationCategory, string> = {
  numerical: '数値整合性',
  'data-quality': 'データ品質',
};

/** @graph-connects none */
function VerificationSummarySection({ verification }: { verification: VerificationResult }) {
  const okCount = verification.items.filter((i) => i.ok).length;
  const totalCount = verification.items.length;
  const categories: VerificationCategory[] = ['numerical', 'data-quality'];

  return (
    <div className="mb-6">
      <div className="mb-3 flex items-center gap-2 text-sm font-bold">
        検算結果サマリ
        <span
          className="rounded px-2 py-0.5 text-[0.625rem] font-bold"
          style={{
            background: verification.allOk ? '#f0fdf4' : '#fef2f2',
            color: verification.allOk ? '#16a34a' : '#dc2626',
          }}
        >
          {String(okCount)}/{String(totalCount)} {verification.allOk ? 'OK' : 'NG'}
        </span>
      </div>
      <div
        className="rounded-xl p-4"
        style={{
          background: 'var(--color-bg-base)',
          border: `1px solid ${verification.allOk ? '#16a34a' : '#dc2626'}`,
        }}
      >
        <div className="flex flex-col gap-1">
          {categories.map((cat) => {
            const catItems = verification.items.filter((i) => i.category === cat);
            if (catItems.length === 0) {
              return null;
            }
            const catOk = catItems.filter((i) => i.ok).length;
            return (
              <div key={cat}>
                <div className="mt-2 border-t border-[var(--color-border)] pt-2 text-[0.625rem] font-semibold tracking-wider text-[var(--color-text-muted)]">
                  {CATEGORY_LABELS[cat]} — {String(catOk)}/{String(catItems.length)} OK
                </div>
                {catItems.map((item) => (
                  <div
                    key={item.label}
                    className="flex flex-wrap items-baseline gap-3 text-xs text-[var(--color-text-secondary)]"
                  >
                    <span className="min-w-[18ch]">{item.label}</span>
                    <span
                      className="ml-auto font-semibold"
                      style={{ color: item.ok ? '#16a34a' : '#dc2626' }}
                    >
                      {item.result} {item.ok ? '✓' : '✗'}
                    </span>
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/** @graph-connects none */
function DeltaSection({ delta }: { delta: DeltaResult }) {
  return (
    <div className="mb-6">
      <div className="mb-3 text-sm font-bold">
        前月差分（{delta.prevMonth} → {delta.currentMonth}）
      </div>
      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-xs">
          <thead>
            <tr className="border-b border-[var(--color-border)]">
              <th className="py-1.5 pr-3 text-left font-semibold text-[var(--color-text-muted)]">
                指標
              </th>
              <th className="py-1.5 px-3 text-right font-semibold text-[var(--color-text-muted)]">
                前月
              </th>
              <th className="py-1.5 px-3 text-right font-semibold text-[var(--color-text-muted)]">
                当月
              </th>
              <th className="py-1.5 px-3 text-right font-semibold text-[var(--color-text-muted)]">
                差分
              </th>
              <th className="py-1.5 px-3 text-right font-semibold text-[var(--color-text-muted)]">
                変動率
              </th>
              <th className="py-1.5 pl-3 text-left font-semibold text-[var(--color-text-muted)]">
                要因
              </th>
            </tr>
          </thead>
          <tbody>
            {delta.entries.map((e) => (
              <tr key={e.metric} className="border-b border-[var(--color-border)]">
                <td className="py-1.5 pr-3 font-medium">{e.metric}</td>
                <td className="py-1.5 px-3 text-right tabular-nums">{yen(e.prevAmount)}</td>
                <td className="py-1.5 px-3 text-right tabular-nums">{yen(e.currentAmount)}</td>
                <td
                  className="py-1.5 px-3 text-right tabular-nums font-semibold"
                  style={{
                    color:
                      e.diff > 0 ? '#dc2626' : e.diff < 0 ? '#16a34a' : 'var(--color-text-muted)',
                  }}
                >
                  {e.diff > 0 ? '+' : ''}
                  {yen(e.diff)}
                </td>
                <td className="py-1.5 px-3 text-right tabular-nums">
                  {e.changeRate !== null
                    ? `${e.changeRate > 0 ? '+' : ''}${String(e.changeRate)}%`
                    : '—'}
                </td>
                <td className="py-1.5 pl-3 text-[var(--color-text-muted)]">{e.reason}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="mt-3 rounded-lg bg-[var(--color-bg-subtle)] p-3 text-[0.6875rem] leading-relaxed text-[var(--color-text-secondary)]">
        <strong className="text-[var(--color-text-primary)]">変動要因サマリ:</strong>{' '}
        {delta.summary}
        <br />
        <strong className="text-[var(--color-text-primary)]">個品数変動:</strong> 前月末{' '}
        <strong>{delta.prevItemCount.toLocaleString('ja-JP')}件</strong> → 当月末{' '}
        <strong>{delta.currentItemCount.toLocaleString('ja-JP')}件</strong>（
        {delta.currentItemCount - delta.prevItemCount >= 0 ? '+' : ''}
        {(delta.currentItemCount - delta.prevItemCount).toLocaleString('ja-JP')}件）
      </div>
    </div>
  );
}

/** @graph-connects none */
function UnknownCostSection({ unknownCostItems }: { unknownCostItems: UnknownCostItem[] }) {
  if (unknownCostItems.length === 0) {
    return null;
  }
  return (
    <div className="mb-6">
      <div className="mb-3 flex items-center gap-2 text-sm font-bold">
        原価不明品
        <span className="rounded bg-[#fffbeb] px-2 py-0.5 text-[0.625rem] font-bold text-[#d97706]">
          {unknownCostItems.length.toLocaleString('ja-JP')}件
        </span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-xs">
          <thead>
            <tr className="border-b border-[var(--color-border)]">
              <th className="py-1.5 pr-3 text-left font-semibold text-[var(--color-text-muted)]">
                個品ID
              </th>
              <th className="py-1.5 px-3 text-left font-semibold text-[var(--color-text-muted)]">
                SKU
              </th>
              <th className="py-1.5 px-3 text-left font-semibold text-[var(--color-text-muted)]">
                ステータス
              </th>
              <th className="py-1.5 px-3 text-left font-semibold text-[var(--color-text-muted)]">
                入荷日
              </th>
              <th className="py-1.5 pl-3 text-left font-semibold text-[var(--color-text-muted)]">
                理由
              </th>
            </tr>
          </thead>
          <tbody>
            {unknownCostItems.slice(0, 20).map((item) => (
              <tr key={item.itemId} className="border-b border-[var(--color-border)]">
                <td className="py-1.5 pr-3 font-medium text-[#1d4ed8]">#{String(item.itemId)}</td>
                <td className="py-1.5 px-3">{item.itemCode}</td>
                <td className="py-1.5 px-3">
                  <span className="rounded bg-[#fffbeb] px-1.5 py-0.5 text-[0.625rem] font-semibold text-[#d97706]">
                    {item.status}
                  </span>
                </td>
                <td className="py-1.5 px-3 tabular-nums">{item.arrivalDate}</td>
                <td className="py-1.5 pl-3 text-[var(--color-text-muted)]">procurement未リンク</td>
              </tr>
            ))}
            {unknownCostItems.length > 20 && (
              <tr>
                <td colSpan={5} className="py-3 text-center text-[var(--color-text-muted)]">
                  … 他 {(unknownCostItems.length - 20).toLocaleString('ja-JP')}件
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/** @graph-connects none */
const USEFUL_LIFE_MONTHS = 24;

/** @graph-connects none */
function elapsedMonths(arrivalDate: string, targetMonth: string): number {
  const arrivalYear = Number(arrivalDate.slice(0, 4));
  const arrivalMonth = Number(arrivalDate.slice(5, 7));
  const targetYear = Number(targetMonth.slice(0, 4));
  const targetMonthNum = Number(targetMonth.slice(5, 7));
  return Math.max(0, (targetYear - arrivalYear) * 12 + (targetMonthNum - arrivalMonth) + 1);
}

/** @graph-connects none */
function ItemTraceSection({
  items,
  targetMonth,
}: {
  items: CostAccountingItem[];
  targetMonth: string;
}) {
  const [selectedId, setSelectedId] = useState<number | null>(items[0]?.itemId ?? null);
  const selectedItem = items.find((i) => i.itemId === selectedId);

  if (items.length === 0) {
    return null;
  }

  return (
    <div className="mb-6">
      <div className="mb-3 text-sm font-bold">個品ドリルダウン（計算トレース）</div>
      <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-bg-base)] p-5">
        <div className="mb-4 flex items-center gap-3 border-b border-[var(--color-border)] pb-3">
          <Search className="h-4 w-4 text-[var(--color-text-muted)]" aria-hidden="true" />
          <select
            value={selectedId ?? ''}
            onChange={(e) => {
              setSelectedId(Number(e.target.value));
            }}
            className="rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-base)] px-3 py-1.5 text-sm text-[var(--color-text-primary)]"
          >
            {items.slice(0, 100).map((item) => (
              <option key={item.itemId} value={item.itemId}>
                #{String(item.itemId)} — {item.itemCode} ({item.status})
              </option>
            ))}
          </select>
          {selectedItem && (
            <span
              className="rounded px-2 py-0.5 text-[0.625rem] font-bold"
              style={{
                background: '#f0fdf4',
                color: '#16a34a',
              }}
            >
              検算OK
            </span>
          )}
        </div>

        {selectedItem &&
          (() => {
            const months = elapsedMonths(selectedItem.arrivalDate, targetMonth);
            const base = Math.floor(selectedItem.acquisitionCost / USEFUL_LIFE_MONTHS);
            const lastMonth = selectedItem.acquisitionCost - base * (USEFUL_LIFE_MONTHS - 1);

            const steps = [
              {
                title: 'Step 1: 入力データ',
                lines: [
                  `取得原価: ${yen(selectedItem.acquisitionCost)}`,
                  `入荷日: ${selectedItem.arrivalDate} (procurement_shippings.arrival_date)`,
                  `ステータス: ${selectedItem.status}`,
                ],
              },
              {
                title: 'Step 2: 経過月数',
                lines: [
                  `${selectedItem.arrivalDate.slice(0, 7)} → ${targetMonth} = ${String(months)}ヶ月目`,
                ],
              },
              {
                title: 'Step 3: 月次償却額',
                lines: [
                  `floor(${selectedItem.acquisitionCost.toLocaleString('ja-JP')} ÷ ${String(USEFUL_LIFE_MONTHS)}) = ${yen(base)} /月`,
                  `※ ${String(USEFUL_LIFE_MONTHS)}ヶ月目: ${selectedItem.acquisitionCost.toLocaleString('ja-JP')} − ${base.toLocaleString('ja-JP')}×${String(USEFUL_LIFE_MONTHS - 1)} = ${yen(lastMonth)}`,
                ],
              },
              {
                title: 'Step 4: 累計償却',
                lines: [yen(selectedItem.cumulativeDepreciation)],
              },
              {
                title: 'Step 5: 帳簿価額',
                lines: [
                  `${selectedItem.acquisitionCost.toLocaleString('ja-JP')} − ${selectedItem.cumulativeDepreciation.toLocaleString('ja-JP')} = ${yen(selectedItem.bookValue)}`,
                ],
              },
              {
                title: 'Step 6: イベント判定',
                lines: [
                  selectedItem.eventType
                    ? `${selectedItem.eventType} (${selectedItem.eventDate ?? '日付不明'}) — 計上額: ${yen(selectedItem.eventCost ?? 0)}`
                    : 'なし（償却継続中）',
                ],
              },
            ];

            return (
              <div className="flex flex-col gap-0">
                {steps.map((step, idx) => (
                  <div key={step.title} className="grid grid-cols-[32px_1fr] gap-3">
                    <div className="flex flex-col items-center">
                      <div
                        className="h-2.5 w-2.5 flex-shrink-0 rounded-full border-2 border-[var(--color-bg-base)]"
                        style={{ background: '#1d4ed8', boxShadow: '0 0 0 2px #1d4ed8' }}
                      />
                      {idx < steps.length - 1 && (
                        <div className="mt-1 w-0.5 flex-1 bg-[var(--color-border)]" />
                      )}
                    </div>
                    <div className="pb-4">
                      <div className="text-xs font-semibold text-[var(--color-text-secondary)]">
                        {step.title}
                      </div>
                      <div className="mt-1 font-mono text-xs leading-relaxed text-[var(--color-text-muted)]">
                        {step.lines.map((line) => (
                          <div key={line}>{line}</div>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            );
          })()}
      </div>
    </div>
  );
}

/**
 * @graph-stack ac-mens-hub
 * @graph-domain Finance
 * @graph-business Detailed verification tab showing 8-item check results with category grouping, month-over-month delta, unknown cost items table, and per-item calculation trace drill-down.
 * @graph-connects none
 */
export function CostAccountingVerificationDetail({
  verification,
  delta,
  items,
  unknownCostItems,
  targetMonth,
}: {
  verification: VerificationResult;
  delta: DeltaResult;
  items: CostAccountingItem[];
  unknownCostItems: UnknownCostItem[];
  targetMonth: string;
}) {
  return (
    <div className="mt-6">
      <VerificationSummarySection verification={verification} />
      <DeltaSection delta={delta} />
      <UnknownCostSection unknownCostItems={unknownCostItems} />
      <ItemTraceSection items={items} targetMonth={targetMonth} />
    </div>
  );
}
